import {
  Autocomplete,
  type AutocompleteChangeReason,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getSmartyAddress } from 'api/smarty/smarty.requests';
import type { SmartyAutoCompleteResponse } from 'api/smarty/smarty.types';
import AddressSearchSuccessIcon from 'assets/icons/circle-check.svg';
import AddressSearchErrorIcon from 'assets/icons/circle-xmark.svg';
import type { AxiosError } from 'axios';
import { defaultDebounceDelay } from 'common/constants';
import { SvgIconWrapper } from 'components/SvgIconWrapper';
import { formatLocation, parseLocation } from 'helpers/utils';
import { debounce, isEmpty, isNaN, isObject, isString } from 'lodash-es';
import { forwardRef, type SyntheticEvent, useImperativeHandle, useRef, useState } from 'react';
import type { IContext } from 'types/IContext';

import { ManualAddressInput } from 'components/ManualAddressInput';
import type { ManualAddressInputRef } from 'components/ManualAddressInput/ManualAddressInput.types';
import type {
  FailedOption,
  SmartyAutoCompleteInputProps,
  SmartyAutoCompleteInputRef,
} from './SmartyAutocompleteInput.types';
import { formatSelectedSmartyOptionWithEntries } from './SmartyAutocompleteInput.utils';

const initialSmartyAddressAutocompleteData = {
  data: [] as SmartyAutoCompleteResponse[],
  loading: false,
  loaded: false,
};

const failedSearchSmartyAddressAutocompleteOptions: FailedOption[] = [
  { type: 'noMatches', label: 'No matches found' },
  { type: 'manualEntry', label: "Can't find my address?" },
];

let smartyAddressRequestController: AbortController;

const SmartyAutocompleteInput = forwardRef<
  SmartyAutoCompleteInputRef | undefined,
  SmartyAutoCompleteInputProps
>(
  (
    {
      isReadOnly,
      label,
      formik,
      fieldName,
      placeholder,
      onSelect,
      state,
      setState,
      includedStates,
      preferStates,
      preferRatio,
      showCantFindMyAddressButton = false,
      ...rest
    },
    ref,
  ) => {
    const [options, setOptions] = useState<IContext<SmartyAutoCompleteResponse[]>>(
      initialSmartyAddressAutocompleteData,
    );

    const [isManualAddressInputActive, setIsManualAddressInputActive] = useState(false);

    const manualAddressInputRef = useRef<ManualAddressInputRef>(undefined);

    const autoCompleteTextInputRef = useRef<HTMLInputElement>(null);

    const [isSelectOpen, setIsSelectOpen] = useState(false);

    const [addressInputState, setAddressInputState] = useState<'pending' | 'error' | 'success'>(
      'pending',
    );

    const getAddressSuggestion = async (search?: string, selected?: string) => {
      try {
        smartyAddressRequestController?.abort();
        smartyAddressRequestController = new AbortController();

        setOptions((prevState) => ({
          ...prevState,
          loading: true,
          loaded: false,
        }));

        const smartyResponse = await getSmartyAddress(
          {
            search,
            selected,
            source: 'all',
            max_results: 10,
            ...(!isEmpty(includedStates) ? { state_filter: includedStates } : {}),
            ...(!isEmpty(preferStates) ? { prefer_states: preferStates } : {}),
            ...(!isNaN(preferRatio) ? { prefer_ratio: preferRatio } : {}),
          },
          smartyAddressRequestController,
        );

        setOptions((prevState) => ({
          ...prevState,
          loading: false,
          loaded: true,
          data: smartyResponse,
        }));

        return smartyResponse;
      } catch (error) {
        const e = error as AxiosError;

        if (e?.message !== 'canceled') {
          setOptions({ ...options, loading: false, loaded: true });
          throw e;
        }

        return null;
      }
    };

    const handleSearch = (event: SyntheticEvent, newValue: string) => {
      if (event?.type === 'change') {
        setAddressInputState('pending');

        if (newValue) {
          setIsSelectOpen(true);
          getAddressSuggestion(newValue);
        }
      }
    };

    const updateState = (selectedOption: SmartyAutoCompleteResponse | string) => {
      const oldVal = state?.[fieldName] as string;
      let val: string | undefined;

      if (isObject(selectedOption)) {
        val = formatLocation(selectedOption).storing;
      } else {
        val = selectedOption;
      }

      formik.setFieldValue(fieldName, val);

      setState?.((prevState) => ({ ...prevState, [fieldName]: val }));

      onSelect?.(val, oldVal);

      setAddressInputState('success');
    };

    const handleChange = (
      e: SyntheticEvent,
      selectedOption: SmartyAutoCompleteResponse | FailedOption | string,
      reason: AutocompleteChangeReason,
    ) => {
      if (reason === 'selectOption') {
        e.preventDefault();

        if (isObject(selectedOption) && 'type' in selectedOption) {
          setIsSelectOpen(false);
          setAddressInputState('pending');
          return;
        }

        if (isObject(selectedOption)) {
          if (selectedOption.entries! > 1) {
            getAddressSuggestion(
              `${selectedOption?.street_line} ${selectedOption?.secondary}`,
              formatSelectedSmartyOptionWithEntries(selectedOption, true),
            );
            setIsSelectOpen(true);
          } else {
            setIsSelectOpen(false);
            updateState(selectedOption);
          }
        } else {
          setIsSelectOpen(false);
          updateState(selectedOption);
        }
      }
    };

    const handleClickAway = () => setIsSelectOpen(false);

    const renderOptionLabel = (option: SmartyAutoCompleteResponse | FailedOption | string) => {
      if (isObject(option) && 'type' in option) {
        return '';
      }

      if (isString(option)) {
        if (!isEmpty(option)) {
          const location = parseLocation(option);
          const formatted = formatLocation({
            ...location,
          });

          return `${formatted.showing.head},${formatted.showing.tail}`.replace(
            /(,\s+|,)(?!.{2,}\w)/g,
            '',
          );
        } else {
          return option;
        }
      } else if (option.entries! > 1) {
        return `${option?.street_line}`;
      } else {
        const formatted = formatLocation(option);

        return `${formatted.showing.head},${formatted.showing.tail}`;
      }
    };

    useImperativeHandle(ref, () => ({
      getAddressInputState: () => addressInputState,

      validateManualAddressInput: async () => manualAddressInputRef?.current?.validate(),

      getIsManualAddressInputActive: () => isManualAddressInputActive,
    }));

    const getAutocompleteOptions = (): (SmartyAutoCompleteResponse | FailedOption)[] => {
      if (!options.loading && options.loaded && (!options.data || options.data.length === 0)) {
        return showCantFindMyAddressButton ? failedSearchSmartyAddressAutocompleteOptions : [];
      }

      return options.data ?? [];
    };

    const shouldMarkManualInputAsError = (
      inputValue: string,
      currentFieldValue: string | undefined,
      inputState: 'pending' | 'success' | 'error' | null,
    ): boolean => {
      if (inputState !== 'pending' || !currentFieldValue) return false;

      const oldValue = renderOptionLabel(parseLocation(currentFieldValue));
      return oldValue !== inputValue;
    };

    const hideManualInputForm = () => {
      setIsManualAddressInputActive(false);

      if (state?.[fieldName] === '-') {
        if (!isEmpty(state?.[`${fieldName}_destructured`])) {
          updateState(
            formatLocation(state?.[`${fieldName}_destructured`] as SmartyAutoCompleteResponse)
              .storing,
          );
        } else {
          updateState('');
          setAddressInputState('pending');
        }
      }
    };

    return isManualAddressInputActive ? (
      <Stack gap={1.4}>
        <ManualAddressInput
          updateAddressField={(address) => {
            updateState(address);

            setAddressInputState('error');
          }}
          useAutoCompleteAddressInput={false}
          includedStates={includedStates}
          preferStates={preferStates}
          initialAddress={autoCompleteTextInputRef?.current?.value}
          isReadOnly={isReadOnly}
          ref={manualAddressInputRef}
        />

        <Typography
          onClick={hideManualInputForm}
          variant="button"
          sx={{
            color: (theme) => theme.palette.customColors.copper,
            cursor: 'pointer',
            textTransform: 'unset',
            width: 'fit-content',
          }}
        >
          Return to address lookup
        </Typography>
      </Stack>
    ) : (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Stack>
          <Autocomplete
            {...rest}
            open={isSelectOpen}
            disableClearable
            disableCloseOnSelect
            isOptionEqualToValue={(option, value) =>
              formatLocation(option as SmartyAutoCompleteResponse).storing === value
            }
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              handleClickAway();

              // Prevent triggering error state if user simply focused & blurred without changing the initial value.
              // This ensures existing backend value doesn't get falsely marked as invalid.
              if (
                shouldMarkManualInputAsError(
                  e.target.value,
                  state?.[fieldName] as string,
                  addressInputState,
                )
              ) {
                updateState(e.target.value);
                setAddressInputState('error');
              }
            }}
            freeSolo
            id={fieldName}
            onFocus={() => setIsSelectOpen(true)}
            filterOptions={(x) => x}
            autoComplete
            value={state?.[fieldName] || ''}
            readOnly={isReadOnly}
            loading={options.loading}
            options={getAutocompleteOptions()}
            onInputChange={debounce(handleSearch, defaultDebounceDelay)}
            onChange={handleChange}
            getOptionLabel={renderOptionLabel}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={autoCompleteTextInputRef}
                placeholder={placeholder}
                name={fieldName}
                label={label}
                sx={[
                  (theme) => (isReadOnly ? theme.mixins.readOnlyInputStyle : {}),
                  (theme) => theme.mixins.placeholderColor(formik, fieldName),
                ]}
                error={formik.touched?.[fieldName] && Boolean(formik.errors?.[fieldName])}
                helperText={formik.touched?.[fieldName] && (formik.errors?.[fieldName] as string)}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    readOnly: isReadOnly,
                    autoComplete: 'off',
                    endAdornment: isReadOnly ? (
                      <></>
                    ) : options.loading ? (
                      <InputAdornment position="end" sx={{ mr: 0.5 }}>
                        <CircularProgress color="inherit" size={20} />
                      </InputAdornment>
                    ) : addressInputState === 'error' ? (
                      <InputAdornment position="end" sx={{ mr: 0.5 }}>
                        <SvgIconWrapper src={AddressSearchErrorIcon} />
                      </InputAdornment>
                    ) : addressInputState === 'success' ? (
                      <InputAdornment position="end" sx={{ mr: 0.5 }}>
                        <SvgIconWrapper src={AddressSearchSuccessIcon} />
                      </InputAdornment>
                    ) : (
                      <></>
                    ),
                  },

                  htmlInput: { ...params.inputProps, 'data-test': fieldName },
                  inputLabel: isReadOnly ? { shrink: true } : {},
                }}
              />
            )}
            renderOption={(props, opt) => {
              if (showCantFindMyAddressButton && opt && typeof opt === 'object' && 'type' in opt) {
                const failedOption = opt as FailedOption;

                switch (failedOption.type) {
                  case 'noMatches':
                    return (
                      <li
                        {...props}
                        key="no-matches"
                        style={{ backgroundColor: 'transparent', cursor: 'auto' }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: (theme) => theme.palette.customColors.disabledActionBar }}
                          width="100%"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {failedOption.label}
                        </Typography>
                      </li>
                    );

                  case 'manualEntry':
                    return (
                      <li
                        {...props}
                        key="manual-entry"
                        style={{ backgroundColor: 'transparent', width: '100%' }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: (theme) => theme.palette.customColors.copper }}
                          fontWeight={700}
                          width="100%"
                          onClick={() => {
                            setIsManualAddressInputActive(true);
                            handleClickAway();
                          }}
                        >
                          {failedOption.label}
                        </Typography>
                      </li>
                    );

                  default:
                    return null;
                }
              }

              const option = opt as SmartyAutoCompleteResponse;

              return (
                <li {...props} key={props.id}>
                  {`${option.street_line},${option.secondary ? ` ${option.secondary}, ` : ' '} ${
                    (option?.entries as number) < 2 ? '' : `(${option.entries} more entries),`
                  } ${option.city}, ${option.state} ${option.zipcode}`}
                </li>
              );
            }}
          />
        </Stack>
      </ClickAwayListener>
    );
  },
);

export default SmartyAutocompleteInput;
